<template>
  <v-card class="pa-6" flat>
    <div class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center mb-4 gap-2">
      <div>
        <p class="grey--text text-body-2 mt-1 mb-0">
          لیست محصولات فعال متصل به مارکت‌پلیس‌ها، متادیتا و تنظیمات قیمت‌گذاری هوشمند.
        </p>
      </div>
      <v-btn class="btn-get-code rounded-lg px-6" @click="openCreateModal">
        <v-icon left>mdi-plus-box</v-icon>
        اتصال محصول جدید
      </v-btn>
    </div>

    <!-- جدول نمایش لیستینگ‌ها -->
    <vue-table
      :data="listings"
      :headers="tableColumns"
      :loading.sync="loading"
      @change="onPagination"
    >
      <!-- فیلترهای جدول -->
      <template v-slot:filters="{ props, perPage }">
        <v-row no-gutters class="mb-4">
          <v-col class="pa-2" cols="12" lg="2" md="3">
            <v-select
              v-model="props.length"
              :items="perPage"
              label="تعداد در صفحه"
              dense outlined hide-details class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="4" md="4">
            <v-text-field
              v-model="props.search"
              label="جستجو (کد، عنوان، تنوع)..."
              prepend-inner-icon="mdi-magnify"
              dense outlined clearable hide-details class="rounded-lg"
              @keyup.enter="() => onPagination(props)"
              @click:clear="() => onPagination(props)"
            />
          </v-col>

          <v-col class="pa-2" cols="12" lg="3" md="3">
            <v-select
              v-model="props.marketplace"
              :items="marketplaceOptions"
              label="مارکت‌پلیس"
              dense outlined clearable hide-details class="rounded-lg"
              @change="() => onPagination(props)"
            />
          </v-col>
        </v-row>
      </template>

      <!-- اسلات ردیف -->
      <template v-slot:index="{ item }">
        {{ persian(item.index + 1 + ((listings.current_page || 1) - 1) * (listings.per_page || 10)) }}
      </template>

      <!-- اسلات عنوان محصول و شناسه مارکت -->
      <template v-slot:product_info="{ item }">
        <div class="d-flex align-center">
          <v-avatar v-if="item.metadata && item.metadata.image_src" size="40" rounded class="ml-2 grey lighten-4">
            <img :src="item.metadata.image_src" alt="product" />
          </v-avatar>
          <div>
            <div class="font-weight-bold">{{ item.product?.title || item.metadata?.title || '—' }}</div>
            <div class="grey--text text-caption">
              کد کالا در مارکت: <span class="font-weight-medium ltr-text">{{ item.external_id || '—' }}</span>
              <span v-if="item.external_variant_id" class="mr-2">
                (کد تنوع: <span class="ltr-text">{{ item.external_variant_id }}</span>)
              </span>
            </div>
            <div v-if="item.sku || item.product_sku" class="text-caption text-primary">
              تنوع داخلی: {{ (item.sku || item.product_sku)?.title }} ({{ (item.sku || item.product_sku)?.sku }})
            </div>
          </div>
        </div>
      </template>

      <!-- اسلات مارکت‌پلیس -->
      <template v-slot:marketplace="{ item }">
        <v-chip
          small
          label
          :color="getMarketplaceBadge(item.marketplace).color"
          :text-color="getMarketplaceBadge(item.marketplace).textColor"
          class="font-weight-bold"
        >
          <v-icon left x-small>{{ getMarketplaceBadge(item.marketplace).icon }}</v-icon>
          {{ getMarketplaceBadge(item.marketplace).title }}
        </v-chip>
      </template>

      <!-- اسلات قیمت و استراتژی -->
      <template v-slot:pricing="{ item }">
        <div v-if="item.pricing_shipping || item.shipping_pricing">
          <div class="font-weight-bold">
            {{ persian(((item.pricing_shipping || item.shipping_pricing).price || 0).toLocaleString()) }} تومان
          </div>
          <small v-if="(item.pricing_shipping || item.shipping_pricing).pricing_strategy" class="teal--text text-caption">
            <v-icon x-small color="teal">mdi-brain</v-icon>
            {{ (item.pricing_shipping || item.shipping_pricing).pricing_strategy.title }}
          </small>
        </div>
        <div v-else-if="item.metadata && item.metadata.price_sale">
          <div class="grey--text text-caption">قیمت فروش دیجی‌کالا:</div>
          <span class="font-weight-medium">{{ persian(Number(item.metadata.price_sale).toLocaleString()) }} تومان</span>
        </div>
        <span v-else class="grey--text text-caption">تنظیم نشده</span>
      </template>

      <!-- اسلات موجودی در مارکت -->
      <template v-slot:stock="{ item }">
        <v-chip
          small
          :color="getStockCount(item) > 0 ? 'green lighten-5' : 'red lighten-5'"
          :text-color="getStockCount(item) > 0 ? 'green darken-3' : 'red darken-3'"
          class="font-weight-bold"
        >
          {{ persian(getStockCount(item)) }} عدد
        </v-chip>
      </template>

      <!-- اسلات وضعیت فعال/غیرفعال -->
      <template v-slot:is_active="{ item }">
        <v-switch
          v-model="item.active"
          color="success"
          hide-details
          dense
          :loading="item.loading"
          class="mt-0 pt-0 d-inline-block"
          @change="toggleListingStatus(item)"
        />
      </template>

      <!-- عملیات -->
      <template v-slot:action="{ item }">
        <action-buttons :data="item" :meta="metabutton(item)" />
      </template>
    </vue-table>

    <!-- مدال اتصال/ویرایش -->
    <custom-popup
      v-model="showFormModal"
      :cons="{ title: editMode ? 'ویرایش لیستینگ مارکت‌پلیس' : 'اتصال محصول به مارکت‌پلیس' }"
      max-width="750"
      hide-confirm
      @close="showFormModal = false"
    >
      <listing-form-modal
        v-if="showFormModal"
        :listing="selectedListing"
        :edit-mode="editMode"
        @saved="onListingSaved"
        @close="showFormModal = false"
      />
    </custom-popup>

    <!-- مدال تنظیمات قیمت و ارسال -->
    <marketplace-pricing-modal
      v-if="showPricingModal"
      v-model="showPricingModal"
      :listing-id="selectedListingId"
      @saved="fetchListings"
    />
  </v-card>
</template>

<script>
import ActionButtons from "@/view/components/adapters/qrList/ordersListButton.vue";
import ListingFormModal from "./ListingFormModal.vue";
import MarketplacePricingModal from "./MarketplacePricingModal.vue";

export default {
  name: "MarketplaceListingsActive",
  components: { ActionButtons, MarketplacePricingModal, ListingFormModal },
  props:{
    store_id:{
      type:Number,
      default:null
    }
  },
  data() {
    return {
      loading: false,
      listings: { data: [], total: 0, current_page: 1, per_page: 10 },
      showFormModal: false,
      showPricingModal: false,
      selectedListingId: null,
      selectedListing: null,
      editMode: false,

      marketplaceOptions: [
        { text: "همه مارکت‌ها", value: null },
        { text: "دیجی‌کالا", value: "digikala" },
        { text: "باسلام", value: "basalam" },
        { text: "اسنپ‌شاپ", value: "snappshop" },
      ],

      tableProps: {
        page: 1,
        length: 10,
        search: null,
        marketplace: null,
        active: 1, // فقط فعال‌ها
        store_id:null
      },
    };
  },

  computed: {
    tableColumns() {
      return [
        { value: "index", text: "ردیف", align: "center", sortable: false },
        { value: "product_info", text: "محصول / کد خارجی", align: "start", sortable: false },
        { value: "marketplace", text: "مارکت‌پلیس", align: "center", sortable: true },
        { value: "pricing", text: "قیمت جاری", align: "center", sortable: false },
        { value: "stock", text: "موجودی مارکت", align: "center", sortable: false },
        { value: "is_active", text: "وضعیت", align: "center", sortable: false },
        { value: "action", text: "عملیات", align: "center", sortable: false },
      ];
    },
  },

  mounted() {
    this.fetchListings();
  },

  methods: {
    openDigiManag(item) {
        const variantId = item.external_variant_id;
        const url = `https://seller.digikala.com/pwa/variant-management?filterSearchIn=search_term&filterSearchQuery=${variantId}&page=1`;
        
        window.open(url, '_blank');
      },

      openDigiShow(item) {
        const dkp = item.external_id;
        const url = `https://www.digikala.com/product/dkp-${dkp}/`;
        
        window.open(url, '_blank');
      },
    getStockCount(item) {
      if (item.pricing_shipping?.stock !== undefined) return item.pricing_shipping.stock;
      if (item.shipping_pricing?.stock !== undefined) return item.shipping_pricing.stock;
      if (item.metadata?.marketplace_seller_stock !== undefined) return item.metadata.marketplace_seller_stock;
      return 0;
    },

    metabutton(item) {
      const buttons = [
        {
          name: "translateShowDigi",
          label: "نمایش در دیجی‌کالا",
          icon: "open-in-new", // تغییر از eye به open-in-new
          class: "btn-show rounded-pill",
          action: (row) => this.openDigiShow(row),
        },
        {
          name: "translateManagementDigi",
          label: "مدیریت در دیجی‌کالا",
          icon: "store-edit-outline",
          class: "btn-buy rounded-pill",
          action: (row) => this.openDigiManag(row),
        },

        // {
        //   name: "edit",
        //   label: "ویرایش",
        //   icon: "pencil-outline",
        //   class: "btn-edit rounded-pill",
        //   action: (row) => this.openEditModal(row),
        // },
      ];

      const pricingData = item.pricing_shipping || item.shipping_pricing;
      if (pricingData) {
        if (pricingData.pricing_strategy_id || pricingData.pricing_strategy) {
          buttons.push({
            name: "apply_strategy",
            label: "اعمال استراتژی",
            icon: "calculator",
            class: "btn-show rounded-pill",
            action: (row) => this.applyStrategyDirectly(row),
          });
        }
        buttons.push({
          name: "delete_pricing",
          label: "حذف قیمت‌گذاری",
          icon: "cash-remove",
          class: "btn-delete rounded-pill",
          action: (row) => this.deletePricingSettingsDirectly(row),
        });
      }

      if (item.marketplace === "digikala") {
        buttons.push({
          name: "sync",
          label: "همگام‌سازی دیجی‌کالا",
          icon: "refresh",
          class: "btn-buy rounded-pill",
          action: (row) => this.syncDigikala(row),
        });
      }

      buttons.push(
        {
          name: "unlink",
          label: "قطع اتصال",
          icon: "link-off",
          class: "btn-edit rounded-pill",
          action: (row) => this.unlinkListing(row),
        },
        {
          name: "delete",
          label: "حذف",
          icon: "trash-can-outline",
          class: "btn-delete rounded-pill",
          action: (row) => this.deleteListing(row),
        }
      );

      return { buttons };
    },

    getMarketplaceBadge(marketplace) {
      const map = {
        digikala: { title: "دیجی‌کالا", color: "red lighten-5", textColor: "red darken-2", icon: "mdi-shopping" },
        basalam: { title: "باسلام", color: "orange lighten-5", textColor: "orange darken-3", icon: "mdi-storefront" },
        snappshop: { title: "اسنپ‌شاپ", color: "green lighten-5", textColor: "green darken-2", icon: "mdi-truck-fast" },
      };
      return map[marketplace] || { title: marketplace || "نامشخص", color: "grey lighten-3", textColor: "grey darken-2", icon: "mdi-web" };
    },

    async fetchListings() {
      this.tableProps.store_id= this.store_id;
      this.loading = true;
      const params = {};
      Object.keys(this.tableProps).forEach((key) => {
        if (this.tableProps[key] !== null && this.tableProps[key] !== "") {
          params[key] = this.tableProps[key];
        }
      });
      if(!this.tableProps.store_id || this.tableProps.store_id==='general_placemarket'){
        delete params.store_id;
      }
      try {
        const { data } = await this.$api.get("/v1/marketplace-listings", { params });
        this.listings = data.data || data;
      } catch (e) {
        this.$toast.error("خطا در دریافت لیست مارکت‌پلیس‌ها");
      } finally {
        this.loading = false;
      }
    },

    onPagination(e) {
      if (e && typeof e === "object" && !Array.isArray(e)) {
        this.tableProps = { ...this.tableProps, ...e, active: 1 };
        if ("search" in e || "marketplace" in e) {
          this.tableProps.page = 1;
        }
      }
      this.fetchListings();
    },

    openCreateModal() {
      this.selectedListing = null;
      this.editMode = false;
      this.showFormModal = true;
    },

    async openEditModal(item) {
      this.loading = true;
      try {
        const { data } = await this.$api.get(`/v1/marketplace-listings/${item.id}`);
        this.selectedListing = data.data || data;
        this.editMode = true;
        this.showFormModal = true;
      } catch (e) {
        this.$toast.error("خطا در دریافت اطلاعات این محصول");
      } finally {
        this.loading = false;
      }
    },

    onListingSaved() {
      this.showFormModal = false;
      this.fetchListings();
    },

    async toggleListingStatus(item) {
      this.$set(item, "loading", true);
      try {
        await this.$api.post(`/v1/marketplace-listings/${item.id}/toggle-active`);
        this.$toast.success("وضعیت تغییر کرد.");
        this.fetchListings(); // رفرش برای خروج از لیست فعال‌ها
      } catch (e) {
        item.active = !item.active;
        this.$toast.error("خطا در تغییر وضعیت اتصال");
      } finally {
        this.$set(item, "loading", false);
      }
    },

    async applyStrategyDirectly(item) {
      this.loading = true;
      try {
        await this.$api.post(`/v1/marketplace-pricing-shipping/${item.id}/apply-strategy`, {
          pricing_strategy_id: null,
        });
        this.$toast.success("قیمت جدید محاسبه و اعمال شد.");
        this.fetchListings();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در اعمال استراتژی");
      } finally {
        this.loading = false;
      }
    },

    async deletePricingSettingsDirectly(item) {
      const confirm = await this.$modal.yesNo(
        `آیا از حذف تنظیمات اختصاصی قیمت و ارسال اطمینان دارید؟`
      );
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.delete(`/v1/marketplace-pricing-shipping/${item.id}`);
        this.$toast.success("تنظیمات با موفقیت حذف شد.");
        this.fetchListings();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در حذف تنظیمات");
      } finally {
        this.loading = false;
      }
    },

    async syncDigikala(item) {
      this.loading = true;
      try {
        await this.$api.post(`/v1/marketplace-listings/${item.id}/sync-digi`);
        this.$toast.success("اطلاعات از کش دیجی‌کالا به‌روزرسانی شد.");
        this.fetchListings();
      } catch (e) {
        this.$toast.error("خطا در همگام‌سازی با دیجی‌کالا");
      } finally {
        this.loading = false;
      }
    },

    async unlinkListing(item) {
      const confirm = await this.$modal.yesNo(
        `آیا از قطع اتصال این محصول از ${item.marketplace} اطمینان دارید؟`
      );
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.post(`/v1/marketplace-listings/${item.id}/unlink`);
        this.$toast.success("اتصال قطع شد.");
        this.fetchListings();
      } catch (e) {
        this.$toast.error("خطا در قطع اتصال");
      } finally {
        this.loading = false;
      }
    },

    async deleteListing(item) {
      const confirm = await this.$modal.yesNo(`آیا از حذف لیستینگ این محصول اطمینان دارید؟`);
      if (!confirm) return;

      this.loading = true;
      try {
        await this.$api.delete(`/v1/marketplace-listings/${item.id}`);
        this.$toast.success("لیستینگ با موفقیت حذف شد.");
        this.fetchListings();
      } catch (e) {
        this.$toast.error("خطا در حذف لیستینگ");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
